import * as React from 'react';
import { capitalize } from 'lodash';
import { logger } from '@common';
import { CircleCheckIcon, TriangleExclamationIcon } from '@revfluence/fresh-icons/regular/esm';

import { Button, Tag, message } from '@revfluence/fresh';

import { useDeprecateAuthProviderMutation } from '@frontend/app/hooks';
import GrantAccessDialog from './GrantAccessDialog';

const { useState, useCallback } = React;

interface IActionCellUser {
  id: string;
  name: string;
  email: string;
}

interface IActionCellProps {
  resourceId: number;
  email: string;
  isShared: boolean;
  clientId: string;
  users?: IActionCellUser[];
  onGranted: () => void;
}

const ActionCell: React.FC<React.PropsWithChildren<IActionCellProps>> = React.memo(
  ({ resourceId, email, isShared, clientId, users, onGranted }) => {
    const [localState, setLocalState] = useState<'default' | 'success' | 'error'>('default');
    const [disconnect, { loading }] = useDeprecateAuthProviderMutation({
      onCompleted() {
        setLocalState('success');
      },
      onError(error) {
        setLocalState('error');
        logger.error(`Failed to disconnect emails ${email}: ${error}`, error);
        message.error('Failed to disconnect. Please try refreshing.');
      },
      context: {
        headers: {
          'requested-client-id': clientId,
        },
      },
    });

    const onDisconnectClick = useCallback(() => {
      disconnect({
        variables: {
          userExternalDisplayId: email,
        },
      });
    }, [email, disconnect]);

    const renderState = useCallback(
      () => (
        <Tag icon={localState === 'error' ? <TriangleExclamationIcon /> : <CircleCheckIcon />} color={localState}>
          {capitalize(localState)}
        </Tag>
      ),
      [localState],
    );

    const renderDefault = useCallback(
      () => (
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Shared mailboxes already give every user on the client access — a grant is
              only meaningful for an individual (non-shared) mailbox. */}
          {!isShared && users && (
            <GrantAccessDialog resourceId={resourceId} clientId={clientId} users={users} onGranted={onGranted} />
          )}
          <Button type="primary" danger onClick={onDisconnectClick} loading={loading}>
            Disconnect
          </Button>
        </div>
      ),
      [onDisconnectClick, loading, isShared, users, resourceId, clientId, onGranted],
    );

    const renderCell = useCallback(() => {
      if (localState === 'success' || localState === 'error') return renderState();
      return renderDefault();
    }, [localState, renderState, renderDefault]);

    return renderCell();
  },
);
ActionCell.displayName = 'ActionCell';

export default ActionCell;
